import type { ProductComparisonMatch } from '@fig/shared';
import { Shield, TrendingUp } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { bestPremiumBonus, issueAgeBonusLabel, premiumBandLabel } from '@/lib/rate-bands';
import { cn } from '@/lib/utils';

interface ProductCardProps {
  match: ProductComparisonMatch;
  isBestMatch?: boolean;
}

function getBestBonus(match: ProductComparisonMatch): number | null {
  return bestPremiumBonus(match.premiumBonuses)?.theRate ?? null;
}

function getBestCommission(match: ProductComparisonMatch): number | null {
  if (!match.commissions || match.commissions.length === 0) return null;
  let best = 0;
  for (const c of match.commissions) {
    if (c.commissionPct != null && c.commissionPct > best) best = c.commissionPct;
  }
  return best > 0 ? best : null;
}

function getIncomeRiderInfo(match: ProductComparisonMatch): {
  name: string;
  fee: string | null;
} | null {
  const riders = match.riders ?? [];
  const incomeRider = riders.find(
    (r) => r.withdrawalBenefitType != null && r.withdrawalBenefitType !== ''
  );
  if (!incomeRider) return null;
  return { name: incomeRider.riderName, fee: incomeRider.feeRateText };
}

/** Find S&P 500 PTP rate for prominent display */
function getSP500Rate(match: ProductComparisonMatch): {
  rate: number;
  rateType: string;
  accountName: string;
} | null {
  const comparisons = match.rateComparison?.strategyComparisons ?? [];
  for (const s of comparisons) {
    const upper = s.accountName.toUpperCase();
    const source = (s.accountSourceCode ?? '').toUpperCase();
    const isSP500 =
      upper.includes('S&P') ||
      upper.includes('SP500') ||
      source.includes('SP500') ||
      source.includes('S&P');
    const isPTP = upper.includes('POINT') || upper.includes('PTP') || upper.includes('ANNUAL');
    if (isSP500 && isPTP) {
      return { rate: s.newRate, rateType: s.rateType, accountName: s.accountName };
    }
  }
  return null;
}

function formatRate(rate: number, rateType: string): string {
  if (rateType === 'participation') return `${rate}% participation`;
  if (rateType === 'fixed') return `${rate.toFixed(2)}% fixed`;
  if (rateType === 'spread') return `${rate.toFixed(2)}% spread`;
  return `${rate.toFixed(2)}% cap`;
}

/** Format withdrawal allowed string for display. ARW returns "10%", "5%", "Interest Only", etc. */
function formatWithdrawalAllowed(
  allowed: string | boolean,
  allowedYear1: string | boolean
): string {
  // Handle legacy boolean values
  if (typeof allowed === 'boolean') {
    return allowed ? (allowedYear1 ? 'Yr 1' : 'Yes') : 'No';
  }
  const val = allowed?.trim();
  if (!val || val === '' || val === 'No' || val === '0%' || val === 'false') return 'No';
  // If it's a percentage, show it
  if (val.includes('%') || val.includes('Interest')) return val;
  if (val === 'Yes' || val === 'true') return 'Yes';
  return val;
}

export function ProductCard({ match, isBestMatch = false }: ProductCardProps) {
  const bestBonus = getBestBonus(match);
  const bonusTier = issueAgeBonusLabel(match.premiumBonuses);
  const bandLabel = premiumBandLabel(match.accounts);
  const bestCommission = getBestCommission(match);
  const incomeRider = getIncomeRiderInfo(match);
  const sp500 = getSP500Rate(match);

  return (
    <Card
      className={cn(
        'product-card-top-depth relative z-10 flex flex-1 flex-col overflow-hidden bg-detail-surface',
        isBestMatch ? 'ring-2 ring-primary shadow-elevation-2' : 'shadow-elevation-2'
      )}
    >
      <CardContent className="p-5 flex flex-col flex-1">
        {/* Header: name + rating (rating stays left of refresh button area) */}
        <div className="flex items-start justify-between">
          <div className="min-w-0 flex-1 pr-2">
            <TruncatedTooltip text={match.annuityName} className="text-sm font-semibold" />
            <TruncatedTooltip text={match.carrierName} className="text-sm text-muted-foreground" />
          </div>
          {/* Rating badge — leaves room for the refresh button overlay from ProductCardGrid */}
          {match.bestRating && (
            <Badge variant="secondary" className="mr-8 shrink-0">
              {match.bestRating}
            </Badge>
          )}
        </div>

        {/* S&P 500 PTP rate — primary rate display */}
        {sp500 && (
          <div className="mt-3 rounded-md border border-border/80 bg-rate-inset p-3 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <TruncatedTooltip
                text={sp500.accountName}
                className="text-xs font-medium text-primary"
              />
              <span className="shrink-0 text-sm font-bold">
                {formatRate(sp500.rate, sp500.rateType)}
              </span>
            </div>
            {bandLabel && <p className="mt-1 text-[10px] text-muted-foreground">{bandLabel}</p>}
          </div>
        )}

        {/* Key metrics grid */}
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Surrender</span>
            <span className="font-medium">
              {match.surrenderPeriodYears != null ? `${match.surrenderPeriodYears}yr` : '—'}
            </span>
          </div>
          {bestBonus != null && (
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">Bonus</span>
              <span className="text-right font-semibold text-success">
                {bestBonus}%
                {bonusTier && (
                  <span className="block text-[10px] font-normal text-muted-foreground">
                    {bonusTier}
                  </span>
                )}
              </span>
            </div>
          )}
          {bestCommission != null && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Commission</span>
              <span className="font-semibold">{bestCommission}%</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Free W/D</span>
            <span className="font-medium">
              {formatWithdrawalAllowed(match.withdrawalAllowed, match.withdrawalAllowedYear1)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Strategies</span>
            <span className="font-medium">{match.accountCount ?? 0}</span>
          </div>
          {match.incomeRiderQuote ? (
            <div className="col-span-2 flex justify-between border-t border-border/50 pt-1.5 mt-0.5">
              <span className="text-muted-foreground">Income</span>
              <span className="font-semibold">
                ${match.incomeRiderQuote.incomeAmount.toLocaleString()}/yr
                <span className="ml-1 text-muted-foreground font-normal">
                  ({match.incomeRiderQuote.benefitRate}% at age {match.incomeRiderQuote.benefitAge})
                </span>
              </span>
            </div>
          ) : incomeRider ? (
            <div className="col-span-2 flex justify-between border-t border-border/50 pt-1.5 mt-0.5">
              <span className="text-muted-foreground truncate mr-2">Income Rider</span>
              <span className="font-medium shrink-0">{incomeRider.fee ?? '—'}</span>
            </div>
          ) : null}
        </div>

        {/* Feature badges */}
        <div className="mt-3 flex flex-wrap gap-1.5">
          {match.mva && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              <Shield className="mr-0.5 h-2.5 w-2.5" />
              MVA
            </Badge>
          )}
          {match.rop && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              <TrendingUp className="mr-0.5 h-2.5 w-2.5" />
              ROP
            </Badge>
          )}
          {match.withdrawalInterest && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              Interest W/D
            </Badge>
          )}
        </div>

        {/* Match score bar — pushed to bottom */}
        <div className="mt-auto pt-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Match Score</span>
            <span className="font-medium">{match.recommendationScore}</span>
          </div>
          <div
            className="relative mt-1 h-2 w-full overflow-hidden"
            style={{ backgroundColor: 'hsl(var(--muted) / 0.15)' }}
          >
            <div
              className="absolute inset-y-0 left-0 transition-all duration-300"
              style={{
                width: `${match.recommendationScore}%`,
                backgroundColor:
                  match.recommendationScore >= 70
                    ? 'hsl(var(--success) / 0.5)'
                    : match.recommendationScore >= 50
                      ? 'hsl(var(--warning) / 0.5)'
                      : 'hsl(var(--destructive) / 0.5)',
                clipPath: 'polygon(0 0, calc(100% - 6px) 0, 100% 100%, 0 100%)',
              }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
